// Hooks do domínio de SUPORTE (chamado aberto no Disk, atendido no Jira).
// Mesmo padrão de use-costs.ts:
//   - `src/api/support.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// Duas exceções deliberadas, no espírito de "consumidor imperativo continua
// imperativo": a tela do chamado envia resposta e alterna fechamento dentro de
// `async function` com estado local (`enviando`, `erroEnvio`), e o diálogo de
// abrir chamado tem um `mutationFn` que fecha sobre meia dúzia de campos do
// formulário. As duas continuam importando `@/api/support` direto e usam daqui
// só as chaves e o invalidador.
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupportConfig, getSupportTicket, listSupportTickets } from "@/api/support";

// As chaves DESTE domínio, como já estavam nas telas — string por string.
//
// Duas ressalvas herdadas:
//  - `config` NÃO leva a marca ativa, mas `tickets`/`ticket` levam. A feature é
//    ligada por org no back, então em rigor a config também seria por marca;
//    na prática a mesma conta tem a mesma configuração de Jira, e partir a
//    entrada agora faria a sidebar rebuscar a cada troca de marca. Divergência
//    pré-existente, preservada;
//  - `ticket` leva orgId ANTES do id (["support-ticket", orgId, id]), o que
//    torna ["support-ticket", orgId] um prefixo útil — mas ninguém o usa hoje:
//    quem escreve invalida a chave completa e a lista.
export const supportKeys = {
  config: ["support-config"] as const,
  tickets: (orgId: string | undefined) => ["support-tickets", orgId] as const,
  ticket: (orgId: string | undefined, id: string) => ["support-ticket", orgId, id] as const,
};

// Gate fail-closed da feature. `retry` é parâmetro porque as pontas divergem e
// a divergência é preservada: a navegação (src/lib/support-enabled.ts) usa
// `retry: false` — item de menu que some é melhor que menu que treme —, e as
// quatro telas usam o default do cliente. Mesma chave, mesma entrada de cache.
export function useSupportConfig(options: { retry?: boolean } = {}) {
  return useQuery({
    queryKey: supportKeys.config,
    queryFn: getSupportConfig,
    ...(options.retry === undefined ? {} : { retry: options.retry }),
  });
}

// A lista inteira numa chamada só, sem polling: o rate limit da Atlassian é da
// conta inteira. `enabled` espera a config CONFIRMADA como ligada.
export function useSupportTickets(orgId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: supportKeys.tickets(orgId),
    queryFn: listSupportTickets,
    enabled: options.enabled,
  });
}

export function useSupportTicket(
  orgId: string | undefined,
  id: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: supportKeys.ticket(orgId, id),
    queryFn: () => getSupportTicket(id),
    enabled: options.enabled,
  });
}

// Responder e alternar fechamento mexem no MESMO par (o chamado e a lista), e
// nesta ordem. `useCallback` porque a tela a chama de dentro de handlers async
// e ela pode acabar em array de dependência.
export function useInvalidateSupportTicket(orgId: string | undefined, id: string) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: supportKeys.ticket(orgId, id) });
    // A lista mostra o mesmo estado: sem invalidar as duas, voltar para
    // "Meus chamados" mostraria o chamado aberto por até 5 min (staleTime).
    await queryClient.invalidateQueries({ queryKey: supportKeys.tickets(orgId) });
  }, [queryClient, orgId, id]);
}
